/**
 * Fast Article Scraper — 3-path strategy in priority order:
 *
 * 1. WordPress REST API   — direct JSON, no proxy, instant (works for most WP blogs)
 * 2. Parallel Sitemap     — all sitemap paths raced simultaneously (first winner returned)
 * 3. Homepage fallback    — last resort: scrape links + fetch metadata
 *
 * Proxy strategy: all 3 CORS proxies raced simultaneously (Promise.any)
 * Enrichment: skipped when sitemap already provides image + date (most modern WP sites)
 */

/* ------------------------------------------------ */
/* CORS PROXY — race all simultaneously              */
/* ------------------------------------------------ */

const CORS_PROXIES = [
  'https://api.allorigins.win/raw?url=',
  'https://corsproxy.io/?',
  'https://api.codetabs.com/v1/proxy?quest='
];

async function fetchWithProxy(url, options = {}) {
  const timeoutMs = options?.timeoutMs || 4000;

  const tryProxy = (proxy) =>
    new Promise((resolve, reject) => {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), timeoutMs);
      fetch(proxy + encodeURIComponent(url), {
        signal: controller.signal,
        headers: {
          Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'User-Agent': 'Mozilla/5.0',
          ...options?.headers
        }
      })
        .then(res => {
          clearTimeout(tid);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          resolve(res);
        })
        .catch(err => {
          clearTimeout(tid);
          reject(err);
        });
    });

  // Race all proxies — return the first one that succeeds
  return Promise.any(CORS_PROXIES.map(tryProxy));
}

function safeFetch(url, options = {}) {
  if (
    url?.includes('/wp-json/') ||
    url?.includes('/wp-admin/') ||
    url?.includes('/rankmath/')
  ) {
    return fetch(url, options);
  }
  return fetchWithProxy(url, options);
}

/* ------------------------------------------------ */
/* URL HELPERS                                       */
/* ------------------------------------------------ */

export function normalizeWebsiteUrl(url) {
  if (!url) return '';
  let normalized = String(url).trim().replace(/\/$/, '');
  if (!/^https?:\/\//i.test(normalized)) normalized = 'https://' + normalized;
  return normalized;
}

export function extractDomain(url) {
  try {
    return new URL(normalizeWebsiteUrl(url)).hostname;
  } catch {
    return '';
  }
}

export function getWebsiteLogo(websiteUrl) {
  const domain = extractDomain(websiteUrl);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null;
}

function safeAbsUrl(u, baseUrl) {
  try {
    return new URL(u, baseUrl).href;
  } catch {
    return null;
  }
}

/* ------------------------------------------------ */
/* MAIN PIPELINE                                     */
/* ------------------------------------------------ */

function describeFetchError(error) {
  if (error?.name === 'AbortError') return 'request timed out';
  return error?.message || String(error || 'unknown error');
}

function recordFetchError(diagnostics, source, error) {
  if (!diagnostics) return;
  diagnostics.errors.push(`${source}: ${describeFetchError(error)}`);
}

export async function fetchArticlesFast(websiteUrl, limit = 20, page = 1, options = {}) {
  const baseUrl = normalizeWebsiteUrl(websiteUrl);
  const diagnostics = { reachable: false, errors: [] };
  const throwOnFailure = options?.throwOnFailure === true;

  if (!extractDomain(baseUrl)) {
    const error = new Error('The selected website has an invalid WordPress URL.');
    if (throwOnFailure) throw error;
    console.error('fetchArticlesFast failed:', error);
    return [];
  }

  try {
    // 1. WordPress REST API — fastest path, supports true server-side pagination
    const wpArticles = await tryWordPressApi(baseUrl, limit, page, diagnostics);
    if (wpArticles.length >= 1) return wpArticles;

    // 2. Sitemaps — fetch total needed for this page, then slice
    const totalNeeded = limit * page;
    const sitemapArticles = await discoverSitemapsParallel(baseUrl, totalNeeded, diagnostics);
    if (sitemapArticles.length >= 1) {
      const sorted = [...sitemapArticles].sort(
        (a, b) => new Date(b.publishDate || 0) - new Date(a.publishDate || 0)
      );
      const pageSlice = sorted.slice((page - 1) * limit, page * limit);
      return pageSlice.length > 0 ? enrichIfNeeded(pageSlice, baseUrl, limit) : [];
    }

    // 3. Homepage fallback — only page 1
    if (page === 1) {
      const homepageArticles = await scrapeHomepage(baseUrl, limit, diagnostics);
      if (homepageArticles.length >= 1) return homepageArticles;
    }

    if (throwOnFailure && !diagnostics.reachable) {
      const details = [...new Set(diagnostics.errors)].slice(0, 3).join('; ');
      throw new Error(
        `Unable to fetch published articles from ${extractDomain(baseUrl)}.` +
        (details ? ` ${details}` : ' The WordPress API, sitemaps, and homepage could not be reached.')
      );
    }

    return [];
  } catch (err) {
    console.error('fetchArticlesFast failed:', err);
    if (throwOnFailure) throw err;
    return [];
  }
}

/* ------------------------------------------------ */
/* PATH 1: WordPress REST API                        */
/* ------------------------------------------------ */

async function tryWordPressApi(baseUrl, limit, page = 1, diagnostics = null) {
  let tid;
  try {
    const controller = new AbortController();
    tid = setTimeout(() => controller.abort(), 5000);

    const url = `${baseUrl}/wp-json/wp/v2/posts?per_page=${Math.min(limit, 100)}&page=${page}&_embed=wp:featuredmedia&orderby=date&order=desc`;
    const res = await fetch(url, { signal: controller.signal });

    if (!res.ok) {
      recordFetchError(diagnostics, 'WordPress REST API', `HTTP ${res.status}`);
      return [];
    }
    if (diagnostics) diagnostics.reachable = true;
    const posts = await res.json();
    if (!Array.isArray(posts) || posts.length === 0) return [];

    return posts.slice(0, limit).map(post => ({
      articleUrl: post.link,
      title: clean(post.title?.rendered || '') || titleFromUrl(post.link),
      publishDate: post.date || null,
      featuredImageUrl:
        post._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
        post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.large?.source_url ||
        post._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium_large?.source_url ||
        post.yoast_head_json?.og_image?.[0]?.url ||
        post.external_featured_image ||
        null,
      metaDescription: clean(post.yoast_head_json?.description || post.yoast_head_json?.og_description || ''),
      excerpt: clean(post.excerpt?.rendered || post.yoast_head_json?.description || ''),
      recipeCardDescription: extractRecipeCardDescription(post.content?.rendered || ''),
      focusKeyword: extractMainKeywordLocal(clean(post.title?.rendered || ''), '', post.link)
    }));
  } catch (error) {
    recordFetchError(diagnostics, 'WordPress REST API', error);
    return [];
  } finally {
    clearTimeout(tid);
  }
}

/* ------------------------------------------------ */
/* PATH 2: Parallel sitemap discovery               */
/* ------------------------------------------------ */

const SITEMAP_PATHS = [
  '/sitemap_index.xml',
  '/sitemap.xml',
  '/wp-sitemap.xml',
  '/post-sitemap.xml',
  '/sitemap-posts.xml',
  '/blog-sitemap.xml',
  '/article-sitemap.xml',
  '/news-sitemap.xml',
  '/recipe-sitemap.xml',
  '/page-sitemap.xml'
];

async function discoverSitemapsParallel(baseUrl, limit, diagnostics = null) {
  // All attempts run simultaneously — first with ≥3 articles wins
  const attempts = [
    fetchRobotsAndParseSitemaps(baseUrl, limit, diagnostics),
    ...SITEMAP_PATHS.map(path => parseAnySitemapUrl(baseUrl + path, baseUrl, limit, diagnostics))
  ].map(p =>
    p.then(articles => {
      if (!articles?.length) throw new Error('empty');
      return articles;
    }).catch(() => { throw new Error('failed'); })
  );

  try {
    return await Promise.any(attempts);
  } catch {
    recordFetchError(diagnostics, 'WordPress sitemaps', 'no readable article sitemap was found');
    return [];
  }
}

async function fetchRobotsAndParseSitemaps(baseUrl, limit, diagnostics = null) {
  try {
    const res = await safeFetch(baseUrl + '/robots.txt', { timeoutMs: 3000 });
    if (diagnostics) diagnostics.reachable = true;
    const text = await res.text();

    const sitemapUrls = [...text.matchAll(/Sitemap:\s*(.+)/gi)]
      .map(m => (m[1] || '').trim())
      .filter(Boolean);

    for (const sm of sitemapUrls.slice(0, 5)) {
      const articles = await parseAnySitemapUrl(sm, baseUrl, limit, diagnostics);
      if (articles?.length) return articles;
    }
    return [];
  } catch {
    return [];
  }
}

async function parseAnySitemapUrl(sitemapUrl, baseUrl, limit, diagnostics = null) {
  try {
    const res = await safeFetch(sitemapUrl, {
      timeoutMs: 4000,
      headers: { Accept: 'application/xml,text/xml' }
    });
    if (diagnostics) diagnostics.reachable = true;
    const xml = await res.text();

    // sitemap index — find child sitemaps
    if (/<sitemapindex/i.test(xml)) {
      const childSitemaps = [...xml.matchAll(/<loc>([^<]+)<\/loc>/gi)]
        .map(m => (m[1] || '').trim())
        .filter(Boolean);

      const preferred = childSitemaps.filter(u =>
        /post|posts|article|blog|recipe|news/i.test(u)
      );

      const candidates = (preferred.length ? preferred : childSitemaps).slice(0, 6);

      // Race child sitemaps in parallel too
      const childAttempts = candidates.map(childUrl =>
        safeFetch(childUrl, { timeoutMs: 4000 })
          .then(r => {
            if (diagnostics) diagnostics.reachable = true;
            return r.text();
          })
          .then(childXml => parseSitemapXml(childXml, baseUrl, limit))
          .catch(() => [])
      );

      const results = await Promise.all(childAttempts);
      const winner = results.find(r => r?.length > 0);
      return winner || [];
    }

    return parseSitemapXml(xml, baseUrl, limit);
  } catch {
    return [];
  }
}

function parseSitemapXml(xml, baseUrl, limit) {
  const urlBlocks = [...xml.matchAll(/<url>([\s\S]*?)<\/url>/gi)].map(m => m[1] || '');
  const articles = [];

  for (const block of urlBlocks) {
    const loc = block.match(/<loc>([^<]+)<\/loc>/i)?.[1]?.trim();
    if (!loc || loc.endsWith('.xml')) continue;

    const lastmod = block.match(/<lastmod>([^<]+)<\/lastmod>/i)?.[1]?.trim() || null;
    const img =
      block.match(/<image:loc>([^<]+)<\/image:loc>/i)?.[1]?.trim() ||
      block.match(/<img:loc>([^<]+)<\/img:loc>/i)?.[1]?.trim() ||
      null;

    const absUrl = safeAbsUrl(loc, baseUrl) || loc;

    articles.push({
      articleUrl: absUrl,
      title: titleFromUrl(absUrl),
      publishDate: lastmod,
      featuredImageUrl: img ? normalizeImageUrl(img, baseUrl) : null,
      excerpt: '',
      recipeCardDescription: '',
      focusKeyword: extractMainKeywordLocal(titleFromUrl(absUrl), '', absUrl)
    });

    if (articles.length >= limit) break;
  }

  // Fallback for non-standard sitemaps
  if (!articles.length) {
    const locs = [...xml.matchAll(/<loc>([^<]+)<\/loc>/gi)]
      .map(m => (m[1] || '').trim())
      .filter(u => u && !u.endsWith('.xml'))
      .slice(0, limit);

    return locs.map(u => ({
      articleUrl: safeAbsUrl(u, baseUrl) || u,
      title: titleFromUrl(u),
      publishDate: null,
      featuredImageUrl: null,
      excerpt: '',
      recipeCardDescription: '',
      focusKeyword: extractMainKeywordLocal(titleFromUrl(u), '', u)
    }));
  }

  return articles;
}

/* ------------------------------------------------ */
/* ENRICHMENT — only when sitemap data is missing   */
/* ------------------------------------------------ */

async function enrichIfNeeded(articles, baseUrl, limit) {
  const missing = articles.filter(a => !a.featuredImageUrl || !a.publishDate || !a.recipeCardDescription);

  // If all articles already have image + date, skip enrichment entirely
  if (missing.length === 0) return articles;

  // If most articles are missing data, enrich up to 8 concurrently
  const toEnrich = missing.slice(0, limit);
  const enriched = await mapLimit(toEnrich, 8, async (a) => {
    if (a.featuredImageUrl && a.publishDate && a.recipeCardDescription) return a;
    try {
      const res = await safeFetch(a.articleUrl, { timeoutMs: 3000 });
      const html = await res.text();
      return {
        ...a,
        title: a.title || clean(html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)?.[1]) || titleFromUrl(a.articleUrl),
        featuredImageUrl: a.featuredImageUrl || extractFeaturedImage(html, baseUrl),
        publishDate: a.publishDate || extractPublishDate(html),
        recipeCardDescription: a.recipeCardDescription || extractRecipeCardDescription(html)
      };
    } catch {
      return a;
    }
  });

  // Merge enriched back
  const enrichedMap = new Map(enriched.map(a => [a.articleUrl, a]));
  return articles.map(a => enrichedMap.get(a.articleUrl) || a);
}

/* ------------------------------------------------ */
/* PATH 3: Homepage scraping (last resort)           */
/* ------------------------------------------------ */

async function scrapeHomepage(baseUrl, limit, diagnostics = null) {
  try {
    const res = await safeFetch(baseUrl, { timeoutMs: 4000 });
    if (diagnostics) diagnostics.reachable = true;
    const html = await res.text();
    const links = extractInternalLinks(html, baseUrl);
    const scored = scoreLinks(links, baseUrl)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    const articles = scored.map(l => ({
      articleUrl: l.url,
      title: titleFromUrl(l.url),
      publishDate: null,
      featuredImageUrl: null,
      excerpt: '',
      recipeCardDescription: '',
      focusKeyword: extractMainKeywordLocal(titleFromUrl(l.url), '', l.url)
    }));

    return await mapLimit(articles, 6, async (a) => {
      try {
        const res = await safeFetch(a.articleUrl, { timeoutMs: 3000 });
        const html = await res.text();
        return {
          ...a,
          title: clean(html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i)?.[1]) || clean(html.match(/<title>([\s\S]*?)<\/title>/i)?.[1]) || a.title,
          featuredImageUrl: extractFeaturedImage(html, baseUrl),
          publishDate: extractPublishDate(html),
          recipeCardDescription: extractRecipeCardDescription(html)
        };
      } catch {
        return a;
      }
    });
  } catch (error) {
    recordFetchError(diagnostics, 'Website homepage', error);
    return [];
  }
}

function extractInternalLinks(html, baseUrl) {
  const domain = extractDomain(baseUrl);
  const links = new Set();
  const regex = /href=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html))) {
    const href = match[1];
    if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) continue;
    try {
      const abs = new URL(href, baseUrl).toString();
      if (new URL(abs).hostname !== domain) continue;
      links.add(abs.split('?')[0].split('#')[0]);
    } catch {}
  }
  return [...links];
}

function scoreLinks(links, baseUrl) {
  return links.map(url => {
    let score = 0;
    const path = url.replace(baseUrl, '');
    if (/\/(blog|post|article|news|recipe)\//i.test(path)) score += 60;
    const hyphens = (path.match(/-/g) || []).length;
    if (hyphens >= 2) score += Math.min(hyphens * 6, 30);
    if (/\/\d{4}\/\d{2}\//.test(path)) score += 35;
    if (/(tag|category|author|page|about|contact|login|register|cart|shop|privacy|terms|wp-admin|wp-content|feed|rss|sitemap|search)/i.test(path)) score -= 200;
    if (path.split('/').filter(Boolean).length <= 1) score -= 20;
    return { url, score };
  }).filter(l => l.score > 0);
}

/* ------------------------------------------------ */
/* CONCURRENCY LIMITER                              */
/* ------------------------------------------------ */

async function mapLimit(list, concurrency, mapper) {
  const results = [];
  let idx = 0;
  const workers = Array.from({ length: Math.min(concurrency, list.length) }, async () => {
    while (idx < list.length) {
      const i = idx++;
      results[i] = await mapper(list[i], i);
    }
  });
  await Promise.all(workers);
  return results;
}

/* ------------------------------------------------ */
/* IMAGE EXTRACTION                                 */
/* ------------------------------------------------ */

function extractFeaturedImage(html, baseUrl) {
  let match = html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i);
  if (match?.[1]) return normalizeImageUrl(match[1], baseUrl);

  match = html.match(/<meta[^>]+name=["']twitter:image["'][^>]+content=["']([^"']+)["']/i);
  if (match?.[1]) return normalizeImageUrl(match[1], baseUrl);

  match = html.match(/"image"\s*:\s*\[\s*"([^"]+)"/i) || html.match(/"image"\s*:\s*"([^"]+)"/i);
  if (match?.[1]) return normalizeImageUrl(match[1], baseUrl);

  const lazyMatch = html.match(/<img[^>]+(data-src|data-lazy|data-original)=["']([^"']+)["']/i);
  if (lazyMatch?.[2]) return normalizeImageUrl(lazyMatch[2], baseUrl);

  const srcsetMatch = html.match(/<img[^>]+srcset=["']([^"']+)["']/i);
  if (srcsetMatch?.[1]) {
    const largest = srcsetMatch[1].split(',').map(s => s.trim().split(' ')[0]).pop();
    if (largest) return normalizeImageUrl(largest, baseUrl);
  }

  for (const m of [...html.matchAll(/<img[^>]+src=["']([^"']+)["']/gi)]) {
    const src = m[1];
    if (src && src.length > 20 && !/logo|icon|avatar|sprite|placeholder|data:image/i.test(src)) {
      return normalizeImageUrl(src, baseUrl);
    }
  }
  return null;
}

function extractRecipeCardDescription(html = '') {
  const match = String(html).match(
    /<[^>]+class=["'][^"']*\brecipe-subtitle\b[^"']*["'][^>]*>([\s\S]*?)<\/[^>]+>/i
  );
  return clean(match?.[1] || '').replace(/^Introduction\s*:?\s*/i, '').trim();
}

function normalizeImageUrl(url, baseUrl) {
  try {
    if (url?.startsWith('//')) return 'https:' + url;
    return new URL(url, baseUrl).href;
  } catch {
    return null;
  }
}

/* ------------------------------------------------ */
/* DATE EXTRACTION                                  */
/* ------------------------------------------------ */

function extractPublishDate(html) {
  let m =
    html.match(/<meta[^>]+property=["']article:published_time["'][^>]+content=["']([^"']+)["']/i) ||
    html.match(/<meta[^>]+name=["']publish_date["'][^>]+content=["']([^"']+)["']/i) ||
    html.match(/<meta[^>]+name=["']date["'][^>]+content=["']([^"']+)["']/i);
  if (m?.[1]) return safeDate(m[1]);

  m = html.match(/<time[^>]+datetime=["']([^"']+)["']/i);
  if (m?.[1]) return safeDate(m[1]);

  m = html.match(/"datePublished"\s*:\s*"([^"]+)"/i);
  if (m?.[1]) return safeDate(m[1]);

  return null;
}

function safeDate(input) {
  try {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

/* ------------------------------------------------ */
/* HELPERS                                          */
/* ------------------------------------------------ */

function titleFromUrl(url) {
  try {
    const slug = (url || '').split('/').filter(Boolean).pop() || 'article';
    return decodeURIComponent(slug)
      .replace(/\.(html|php|aspx)$/i, '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  } catch {
    return 'Article';
  }
}

function clean(text = '') {
  return String(text)
    .replace(/<[^>]+>/g, '')
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;|&apos;/g, "'")
    .trim();
}

const KEYWORD_STOP_WORDS = new Set([
  'the', 'and', 'or', 'of', 'with', 'for', 'to', 'a', 'an', 'in', 'on', 'at', 'from',
  'recipe', 'recipes', 'easy', 'best', 'quick', 'simple', 'delicious'
]);

function extractMainKeywordLocal(title, excerpt, url) {
  const sources = [title, excerpt, url?.split('/')?.pop() || '']
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const words = sources
    .split(' ')
    .filter(w => w.length > 2 && !KEYWORD_STOP_WORDS.has(w));

  if (!words.length) return title?.trim()?.split(/\s+/)?.slice(0, 5)?.join(' ') || 'article';
  return words.slice(0, 4).join(' ');
}

/* ------------------------------------------------ */
/* VALIDATION                                       */
/* ------------------------------------------------ */

export function validateArticleForSave(article) {
  const errors = [];
  if (!article?.title) errors.push('Title missing');
  return { isValid: errors.length === 0, errors };
}
